// client/src/pages/secure/PropertyListPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // Import Link
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';
import PageHeader from '../../components/shared/PageHeader';

// --- Add Property Modal Component ---
const AddPropertyModal = ({ isOpen, onClose, onPropertyAdded }) => {
    const [formData, setFormData] = useState({
        street_address: '', city: '', state: 'IL', zip_code: '',
        status: 'For Sale', listing_price: '', property_type: '',
        description: '', bedrooms: '', bathrooms: '', square_footage: '', year_built: ''
    });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            const newProperty = await apiClient.post('/api/properties', formData);
            onPropertyAdded(newProperty);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create property.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto w-full max-w-2xl">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Add New Property</h2>
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input name="street_address" value={formData.street_address} onChange={handleChange} placeholder="Street Address" required className="p-2 border rounded md:col-span-2" />
                        <input name="city" value={formData.city} onChange={handleChange} placeholder="City" required className="p-2 border rounded" />
                        <input name="state" value={formData.state} onChange={handleChange} placeholder="State" required className="p-2 border rounded" />
                        <input name="zip_code" value={formData.zip_code} onChange={handleChange} placeholder="Zip Code" required className="p-2 border rounded" />
                        <select name="status" value={formData.status} onChange={handleChange} className="p-2 border rounded">
                            <option value="For Sale">For Sale</option>
                            <option value="For Lease">For Lease</option>
                            <option value="Pending">Pending</option>
                            <option value="Withdrawn">Withdrawn</option>
                        </select>
                        <input name="listing_price" type="number" value={formData.listing_price} onChange={handleChange} placeholder="Listing Price / Rate" className="p-2 border rounded" />
                        <input name="property_type" value={formData.property_type} onChange={handleChange} placeholder="Property Type (e.g., Office)" className="p-2 border rounded" />
                        <input name="bedrooms" type="number" value={formData.bedrooms} onChange={handleChange} placeholder="Bedrooms" className="p-2 border rounded" />
                        <input name="bathrooms" type="number" step="0.5" value={formData.bathrooms} onChange={handleChange} placeholder="Bathrooms" className="p-2 border rounded" />
                        <input name="square_footage" type="number" value={formData.square_footage} onChange={handleChange} placeholder="Square Footage" className="p-2 border rounded" />
                        <input name="year_built" type="number" value={formData.year_built} onChange={handleChange} placeholder="Year Built" className="p-2 border rounded" />
                        <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Description" rows="3" className="p-2 border rounded md:col-span-2"></textarea>
                    </div>
                    {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save Property'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Property List Page Component ---
const PropertyListPage = () => {
    const [properties, setProperties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const apiClient = useApiClient();

    useEffect(() => {
        apiClient.get('/api/properties')
            .then(data => setProperties(data))
            .catch(err => setError(err.message || 'Failed to fetch properties.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    const handlePropertyAdded = (newProperty) => {
        setProperties(prev => [newProperty, ...prev]);
    };
    
    return (
        <>
            <AddPropertyModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onPropertyAdded={handlePropertyAdded}
            />
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow">
                <PageHeader title="Properties" description="The office's listings and properties for sale or lease. Open one to manage it."
                    actions={
                        <div className="flex flex-wrap gap-2">
                        <Link to="/dashboard/property-search?tab=zoning"
                            className="px-4 py-2 text-sm font-medium text-slate-800 bg-white border border-slate-300 rounded-md hover:bg-slate-50">
                            Zoning search
                        </Link>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                        >
                            Add New Property
                        </button>
                        </div>
                    } />

                {isLoading && <p>Loading properties...</p>}
                {error && <p className="text-red-500">{error}</p>}
                {!isLoading && !error && (
                    <DataList rows={properties} rowKey="property_id" csvName="properties" defaultSort={{ key: 'property_id', dir: 'desc' }}
                        filters={[{ key: 'status', label: 'Status' }, { key: 'city', label: 'City' }, { key: 'property_type', label: 'Type' }]}
                        columns={[
                            { key: 'property_id', label: '#', type: 'number' },
                            { key: 'street_address', label: 'Address', render: (p) => <Link to={`/dashboard/properties/${p.property_id}`} className="text-indigo-700 font-medium">{p.street_address}</Link> },
                            { key: 'city', label: 'City' },
                            { key: 'property_type', label: 'Type' },
                            { key: 'listing_price', label: 'Price', type: 'number', render: (p) => (p.listing_price ? `$${Number(p.listing_price).toLocaleString()}` : '') },
                            { key: 'status', label: 'Status' },
                            { key: 'listing_date', label: 'Listed', type: 'date' },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (p) => <Link to={`/dashboard/properties/${p.property_id}`} className="text-indigo-600 hover:text-indigo-900">Manage</Link> },
                        ]} />
                )}
            </div>
        </>
    );
};

export default PropertyListPage;
